import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#D4AF37', // Prawdziwe złoto
      light: '#E5C76B', // Jaśniejsze złoto
      dark: '#996515', // Ciemniejsze złoto (Old Gold)
    },
    secondary: {
      main: '#B8860B', // Dark Goldenrod
      light: '#CFB53B', // Old Gold
      dark: '#8B6914', // Dark Golden Rod
    },
    background: {
      default: '#0A0A0A',
      paper: 'rgba(20, 20, 20, 0.95)',
      sidebar: 'linear-gradient(135deg, rgba(25,25,25,0.97) 0%, rgba(30,30,30,0.95) 100%)',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    gold: {
      lighter: '#F4E5C3',   // Najjaśniejsze złoto
      light: '#E5C76B',     // Jasne złoto
      main: '#D4AF37',      // Prawdziwe złoto
      dark: '#996515',      // Ciemne złoto
      darker: '#704214',    // Najciemniejsze złoto
      text: '#0A0A0A',      // Tekst na złotym tle
    },
  },
  typography: {
    fontFamily: [
      'Montserrat',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontFamily: 'Cinzel, serif',
      fontWeight: 700,
      letterSpacing: '0.05em',
    },
    h2: {
      fontFamily: 'Cinzel, serif',
      fontWeight: 600,
      letterSpacing: '0.05em',
    },
    h3: {
      fontFamily: 'Cinzel, serif',
      fontWeight: 600,
      letterSpacing: '0.05em',
    },
    h4: {
      fontFamily: 'Cinzel, serif',
      fontWeight: 600,
      letterSpacing: '0.05em',
    },
    h5: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 600,
      letterSpacing: '0.03em',
    },
    h6: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 600,
      letterSpacing: '0.03em',
    },
    subtitle1: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 500,
      fontSize: '1.2rem',
      letterSpacing: '0.02em',
    },
    subtitle2: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 500,
      fontSize: '1.1rem',
      letterSpacing: '0.02em',
    },
    body1: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 400,
      letterSpacing: '0.01em',
    },
    body2: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 400,
      letterSpacing: '0.01em',
    },
    button: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 600,
      letterSpacing: '0.05em',
    },
    caption: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 400,
      letterSpacing: '0.02em',
    },
    overline: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 500,
      letterSpacing: '0.1em',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 5px 15px rgba(212, 175, 55, 0.4)',
          },
        },
        contained: {
          background: 'linear-gradient(45deg, #D4AF37 30%, #996515 90%)',
          color: '#000',
          '&:hover': {
            background: 'linear-gradient(45deg, #E5C76B 30%, #B8860B 90%)',
          },
          '&.MuiButton-containedSecondary': {
            background: 'linear-gradient(45deg, #D4AF37 30%, #996515 90%)',
            color: '#000',
            '&:hover': {
              background: 'linear-gradient(45deg, #E5C76B 30%, #B8860B 90%)',
            },
          },
        },
        outlined: {
          borderColor: '#D4AF37',
          color: '#D4AF37',
          '&:hover': {
            borderColor: '#E5C76B',
            color: '#E5C76B',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(212, 175, 55, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(212, 175, 55, 0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#D4AF37',
            },
          },
          '& .MuiInputLabel-root': {
            fontFamily: 'Montserrat, sans-serif',
            letterSpacing: '0.02em',
          },
          '& .MuiInputBase-input': {
            fontFamily: 'Montserrat, sans-serif',
            letterSpacing: '0.02em',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(20, 20, 20, 0.95)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'linear-gradient(135deg, rgba(25,25,25,0.97) 0%, rgba(30,30,30,0.95) 100%)',
          borderRight: '1px solid rgba(212, 175, 55, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0A0A0A',
          borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(212, 175, 55, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(212, 175, 55, 0.2)',
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#D4AF37',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 500,
          letterSpacing: '0.02em',
        },
        secondary: {
          fontFamily: 'Montserrat, sans-serif',
          letterSpacing: '0.01em',
        },
      },
    },
  },
});

export default theme;
